import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Shield,
  Truck,
  Users,
  UserPlus,
  PhoneCall,
  Settings as SettingsIcon,
  BarChart3,
  LogOut,
  Printer,
  Calendar, Clock,
  Tag,
  CheckCircle,
  Monitor,
  Mail,
  Send,
  Briefcase,
  Bell,
  Menu,
  ChevronDown,
  ChevronRight,
  Database,
  X,
  ChevronLeft,
  ReceiptIndianRupee
} from 'lucide-react';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasFeature, hasPermission } = useAuth();


  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const mainNavItems = [
    { name: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard size={20} /> },
    { name: 'User Calendar', path: '/user-calendar', icon: <Calendar size={20} />, permission: 'followups:read' },
    { name: 'Leads', path: '/leads', icon: <Users size={20} />, permission: 'leads:read' },
    { name: 'Customers', path: '/customers', icon: <Briefcase size={20} />, permission: 'customers:read' },
    { name: 'Deliveries', path: '/deliveries', icon: <Truck size={20} />, permission: 'deliveries:read' },
    { name: 'Proforma Invoice', path: '/invoices', icon: <ReceiptIndianRupee size={20} />, permission: 'invoices:read', feature: 'invoices' },
    { name: 'Reports', path: '/reports', icon: <BarChart3 size={20} />, permission: 'reports:read' },
    { name: 'Attendance', path: '/attendance', icon: <Clock size={20} />, roles: ['Admin'] },
    { name: 'User Management', path: '/users', icon: <Users size={20} />, permission: 'users:read' },
    { name: 'Roles & Permissions', path: '/roles', icon: <Shield size={20} />, permission: 'roles:read' },
    { name: 'Settings', path: '/settings', icon: <SettingsIcon size={20} />, permission: 'settings:read' },
  ];

  const masterNavItems = [
    { name: 'Lead Status', path: '/masters/status', icon: <CheckCircle size={18} />, permission: 'masters:read' },
    { name: 'Delivery Status', path: '/masters/delivery-statuses', icon: <Truck size={18} />, permission: 'masters:read' },
    { name: 'Lead Source', path: '/masters/lead-sources', icon: <Briefcase size={18} />, permission: 'masters:read' },
    { name: 'Customer Type', path: '/masters/customer-types', icon: <Users size={18} />, permission: 'masters:read' },
    { name: 'Machine', path: '/masters/machines', icon: <Monitor size={18} />, permission: 'masters:read' },
  ];

  const filterItem = (item) => {
    if (user?.role === 'Delivery Boy') {
      return item.name === 'Deliveries';
    }
    if (user?.role === 'Admin') return true;
    if (item.roles && item.roles.includes(user?.role)) return true;
    if (item.permission && hasPermission(item.permission)) return true;
    if (!item.roles && !item.feature && !item.permission) return true; // Dashboard
    return false;
  };

  const filteredMainNav = mainNavItems.filter(filterItem);
  const filteredMasterNav = masterNavItems.filter(filterItem);


  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed);
  }, [isCollapsed]);
  const [isMasterOpen, setIsMasterOpen] = useState(false);

  return (
    <div className="app-container">
      {/* Mobile Header */}
      <div className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => setIsSidebarOpen(true)} style={{ color: 'white', background: 'none', border: 'none', padding: '0', cursor: 'pointer' }}>
            <Menu size={28} />
          </button>
          <img src="/logo.png" alt="Abhay Agencies" style={{ height: '40px', backgroundColor: 'white', padding: '4px', borderRadius: '4px' }} />
        </div>
        <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'var(--color-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }} onClick={() => navigate('/profile')}>
          {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
        </div>
      </div>

      {/* Overlay */}
      {isSidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 999 }}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isSidebarOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header" style={{ padding: "1rem", display: "flex", justifyContent: "center", alignItems: "center", position: "relative" }}>
          {!isCollapsed && (
            <img
              src="/logo.png"
              alt="Abhay Agencies"
              style={{
                maxWidth: '80%',
                maxHeight: '50px',
                objectFit: 'contain',
                backgroundColor: 'white',
                padding: '4px',
                borderRadius: '8px'
              }}
            />
          )}
          {isCollapsed && (
            <img
              src="/favicon.ico"
              alt="Icon"
              style={{
                width: '36px',
                height: '36px',
                objectFit: 'contain',
                backgroundColor: 'white',
                padding: '4px',
                borderRadius: '8px'
              }}
            />
          )}

          <button
            className="mobile-close-btn"
            onClick={() => setIsSidebarOpen(false)}
            style={{ background: "none", border: "none", color: "white", cursor: "pointer", display: "none", position: "absolute", right: "1rem" }}
          >
            <X size={24} />
          </button>
        </div>
        <nav className="sidebar-nav hide-scrollbar">
          <div className="sidebar-section-title text-xs font-semibold text-muted uppercase tracking-wider mb-2 px-3 mt-2">Main Menu</div>
          {filteredMainNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/leads'} // Fixes the overlapping active state for /leads and /leads/add
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setIsSidebarOpen(false)}
            >
              {item.icon}
              <span className="nav-text">{item.name}</span>
            </NavLink>
          ))}

          {filteredMasterNav.length > 0 && (
            <div>
              <button
                className="nav-item"
                style={{ width: '100%', border: 'none', background: 'transparent' }}
                onClick={() => {
                  if (isCollapsed) {
                    setIsCollapsed(false);
                    setIsMasterOpen(true);
                  } else {
                    setIsMasterOpen(!isMasterOpen);
                  }
                }}
              >
                <Database size={20} />
                <span className="nav-text" style={{ flex: 1, textAlign: 'left' }}>Masters</span>
                <span className="master-chevron" style={{ display: 'flex', alignItems: 'center' }}>{isMasterOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
              </button>

              {(!isCollapsed && isMasterOpen) && (
                <div className="master-submenu">
                  {filteredMasterNav.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) => `nav-item master-sub-item ${isActive ? 'active' : ''}`}
                      onClick={() => setIsSidebarOpen(false)}
                      style={{ padding: '0.5rem 1rem' }}
                    >
                      {item.icon}
                      <span className="nav-text" style={{ fontSize: "0.85rem" }}>{item.name}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>
        <div className="sidebar-bottom" style={{ padding: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="nav-item hidden-mobile"
            style={{ width: '100%', border: 'none', background: 'transparent', padding: '0.75rem', justifyContent: isCollapsed ? 'center' : 'flex-start' }}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            <span className="nav-text" style={{ fontSize: '0.9rem', fontWeight: '500' }}>Collapse</span>
          </button>

          <div className="profile-btn" onClick={() => navigate('/profile')} style={{ display: 'flex', alignItems: 'center', gap: isCollapsed ? '0' : '0.75rem', padding: '0.5rem', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '12px', cursor: 'pointer', transition: 'background-color 0.2s', justifyContent: isCollapsed ? 'center' : 'flex-start' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}>
            <div className="avatar" style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'var(--color-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>
              {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
            </div>
            <div className="user-profile-info" style={{ overflow: 'hidden' }}>
              <div style={{ color: 'white', fontWeight: '600', fontSize: '0.9rem', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{user?.name || 'User'}</div>
              <div style={{ color: 'var(--color-text-sidebar)', fontSize: '0.75rem', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{user?.role || 'Guest'}</div>
            </div>
          </div>

          <button
            className="nav-item"
            style={{ width: '100%', border: 'none', background: 'transparent', padding: '0.75rem', justifyContent: isCollapsed ? 'center' : 'flex-start' }}
            onClick={handleLogout}
          >
            <LogOut size={20} />
            <span className="nav-text">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <div className="page-container" key={location.pathname}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
